import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

type Theme = 'light' | 'dark';
type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Determines the theme based on current time
 * Dark: 7pm (19:00) to 7am (07:00)
 * Light: 7am (07:00) to 7pm (19:00)
 */
function getAutoTheme(): Theme {
  const hour = new Date().getHours();
  return hour >= 19 || hour < 7 ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Mode determines user preference: light, dark, or auto (follows schedule)
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode') as ThemeMode;
    return saved || 'auto';
  });

  // Actual theme being applied (computed from mode)
  const [theme, setTheme] = useState<Theme>(() => {
    if (mode === 'auto') {
      return getAutoTheme();
    }
    return mode;
  });

  // Update theme when mode changes
  useEffect(() => {
    if (mode === 'auto') {
      setTheme(getAutoTheme());
    } else {
      setTheme(mode);
    }
  }, [mode]);

  // Auto-update theme every minute when in auto mode
  useEffect(() => {
    if (mode !== 'auto') return;

    const updateTheme = () => {
      setTheme(getAutoTheme());
    };

    // Check every minute
    const interval = setInterval(updateTheme, 60000);

    return () => clearInterval(interval);
  }, [mode]);

  // Persist mode to localStorage
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Apply theme to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Cycle through modes: light → dark → auto → light
  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'auto';
      return 'light';
    });
  }, []);

  // Set mode directly
  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const contextValue = useMemo(
    () => ({ theme, mode, toggleTheme, setMode }),
    [theme, mode, toggleTheme, setMode]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
